/**
 * Intégrations — Home Assistant, agenda, webhook
 *
 * Trois réglages qui ouvrent l'application vers l'extérieur, et donc trois
 * endroits où l'interface doit dire la vérité plutôt que rassurer :
 *
 *  - **Home Assistant** : le bouton « tester » affiche le message du serveur tel
 *    quel — « jeton refusé », « rien n'écoute à cette adresse ». Un « échec de la
 *    connexion » générique laisse l'utilisateur deviner, et il devine mal.
 *    Le test précède l'enregistrement : on ne sauvegarde pas une configuration
 *    fausse pour découvrir ensuite qu'elle l'était.
 *
 *  - **Agenda** : régénérer le jeton casse tous les abonnements existants. C'est
 *    dit avant, pas après.
 *
 *  - **Webhook** : il donne le droit de *créer* des tâches. L'écran explique
 *    pourquoi son adresse ne se partage pas comme celle de l'agenda.
 *
 * Les jetons ne sont jamais réaffichés une fois enregistrés — le serveur ne les
 * renvoie pas. Le champ reste donc vide et son étiquette le dit, plutôt que
 * d'afficher des points qui feraient croire qu'on peut les relire.
 */

import { useState } from 'react';
import { CalendarClock, Check, Copy, Link2, RefreshCw, TriangleAlert, Webhook, X } from 'lucide-react';

import { Champ, Chargement, Erreur, Section, TRAIT } from '../composants/ui.js';
import { useAppli, useRessource } from '../lib/appli.js';
import { donnees } from '../lib/donnees.js';

type Entite = { id: string; nom: string; articles: number };

export function Integrations() {
  const { famille, rafraichis } = useAppli();
  const ical = useRessource(() => donnees.ical());
  const webhook = useRessource(() => donnees.webhook());

  const [url, setUrl] = useState(famille?.haUrl ?? '');
  const [jeton, setJeton] = useState('');
  const [entites, setEntites] = useState<Entite[] | null>(null);
  const [choisie, setChoisie] = useState(famille?.haEntiteCourses ?? '');
  const [choisieTaches, setChoisieTaches] = useState(famille?.haEntiteTaches ?? '');
  const [resultat, setResultat] = useState<{ ok: boolean; texte: string } | null>(null);
  const [occupe, setOccupe] = useState(false);
  const [copie, setCopie] = useState<string | null>(null);

  if (ical.chargement && !ical.donnee) return <Chargement quoi="Chargement des intégrations" />;
  if (ical.erreur && !ical.donnee) return <Erreur message={ical.erreur} />;

  async function copieDansPressePapier(texte: string, quoi: string) {
    try {
      await navigator.clipboard.writeText(texte);
      setCopie(quoi);
      setTimeout(() => setCopie(null), 2000);
    } catch {
      // Le presse-papier peut être refusé (contexte non sécurisé, permission) :
      // l'adresse reste sélectionnable à la main, on ne bloque rien.
      setCopie(null);
    }
  }

  async function teste() {
    setOccupe(true);
    setResultat(null);
    try {
      const r = await donnees.testeHa({ url: url.trim(), jeton: jeton.trim() || undefined });
      if (r.ok) {
        setEntites(r.entites);
        setResultat({ ok: true, texte: `Home Assistant ${r.version} répond. ${r.entites.length} liste(s) trouvée(s).` });
        if (!choisie && r.entites.length === 1) setChoisie(r.entites[0]!.id);
      } else {
        setEntites(null);
        setResultat({ ok: false, texte: r.message });
      }
    } catch (e) {
      setResultat({ ok: false, texte: e instanceof Error ? e.message : 'Test impossible' });
    } finally {
      setOccupe(false);
    }
  }

  async function enregistre() {
    setOccupe(true);
    setResultat(null);
    try {
      await donnees.majHa({
        url: url.trim() || null,
        entite: choisie || null,
        entiteTaches: choisieTaches || null,
        ...(jeton.trim() ? { jeton: jeton.trim() } : {}),
      });
      setJeton('');
      setResultat({ ok: true, texte: 'Réglages enregistrés.' });
      rafraichis();
    } catch (e) {
      setResultat({ ok: false, texte: e instanceof Error ? e.message : 'Enregistrement impossible' });
    } finally {
      setOccupe(false);
    }
  }

  async function synchroniseTaches() {
    setOccupe(true);
    setResultat(null);
    try {
      const r = await donnees.synchroniseTachesHa();
      if (r.ok) {
        const b = r.bilan;
        const faits = b
          ? [
              b.cochees && `${b.cochees} cochée(s) dans HA`,
              b.ajoutees && `${b.ajoutees} ajoutée(s)`,
              b.modifiees && `${b.modifiees} mise(s) à jour`,
              b.retirees && `${b.retirees} retirée(s)`,
              b.importees && `${b.importees} importée(s) de HA`,
            ].filter(Boolean)
          : [];
        setResultat({ ok: true, texte: faits.length ? `Synchronisé : ${faits.join(', ')}.` : 'Déjà à jour.' });
      } else {
        setResultat({ ok: false, texte: r.message });
      }
      rafraichis();
    } catch (e) {
      setResultat({ ok: false, texte: e instanceof Error ? e.message : 'Synchronisation impossible' });
    } finally {
      setOccupe(false);
    }
  }

  return (
    <>
      <div className="entete-vue">
        <h1>
          Intégrations
        </h1>
      </div>

      {/* ── Home Assistant ─────────────────────────────────────────────── */}
      <Section
        titre="Home Assistant"
        description="La liste de courses vit là-bas. Les tâches de l’accueil peuvent y être recopiées, et cochées depuis Home Assistant."
      >
        <Champ etiquette="Adresse de Home Assistant">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="http://homeassistant.local:8123"
            inputMode="url"
            autoComplete="off"
          />
          <p className="aide">
            La racine, sans chemin : ni « /lovelace » ni « /config ».
          </p>
        </Champ>

        <Champ
          etiquette={
            famille?.haJetonDefini ? 'Jeton d’accès (déjà enregistré)' : 'Jeton d’accès longue durée'
          }
        >
          <input
            type="password"
            value={jeton}
            onChange={(e) => setJeton(e.target.value)}
            placeholder={famille?.haJetonDefini ? 'Laisser vide pour conserver l’actuel' : 'Collez le jeton'}
            autoComplete="off"
          />
          <p className="aide">
            Il se crée dans Home Assistant, sur votre page de profil, tout en bas — section « Jetons
            d’accès longue durée ». Il n’est jamais réaffiché ensuite.
          </p>
        </Champ>

        <div className="actions">
          <button type="button" className="bouton secondaire" onClick={teste} disabled={occupe || !url.trim()}>
            <Link2 size={18} strokeWidth={TRAIT} />
            Tester la connexion
          </button>
        </div>

        {resultat && (
          <p className={resultat.ok ? 'succes' : 'alerte'}>
            {resultat.ok ? <Check size={16} strokeWidth={TRAIT} /> : <X size={16} strokeWidth={TRAIT} />}
            {resultat.texte}
          </p>
        )}

        {entites && entites.length > 0 && (
          <Champ etiquette="Liste de courses à utiliser">
            <select value={choisie} onChange={(e) => setChoisie(e.target.value)}>
              <option value="">— choisir —</option>
              {entites.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom} ({e.id}) — {e.articles} article{e.articles > 1 ? 's' : ''}
                </option>
              ))}
            </select>
            <p className="aide">Découvertes dans Home Assistant : rien à taper de mémoire.</p>
          </Champ>
        )}

        {entites && entites.length > 0 && (
          <Champ etiquette="Liste des tâches du foyer">
            <select value={choisieTaches} onChange={(e) => setChoisieTaches(e.target.value)}>
              <option value="">— ne pas recopier les tâches —</option>
              {entites
                .filter((e) => e.id !== choisie)
                .map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.nom} ({e.id})
                  </option>
                ))}
            </select>
            <p className="aide">
              Une liste à part, créée pour l’occasion : Foyer y range ce que montre l’accueil — en retard,
              ce mois-ci, sans date — et la tient à jour chaque minute. Cocher dans Home Assistant coche
              dans Foyer ; effacer un élément là-bas ne supprime rien ici.
            </p>
          </Champ>
        )}

        {famille?.haEntiteTaches && (
          <div className={famille.haTachesErreur ? 'alerte' : 'note'}>
            <div>
              Tâches recopiées dans <code>{famille.haEntiteTaches}</code>.{' '}
              {famille.haTachesErreur
                ? `Dernière tentative en échec : ${famille.haTachesErreur}`
                : famille.haTachesSynchroLe
                  ? `Dernière synchronisation : ${new Date(famille.haTachesSynchroLe).toLocaleString('fr-FR', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}.`
                  : 'Première synchronisation dans la minute.'}
            </div>
            <div className="actions">
              <button type="button" className="bouton secondaire" onClick={synchroniseTaches} disabled={occupe}>
                Synchroniser maintenant
              </button>
            </div>
          </div>
        )}

        {entites && entites.length === 0 && (
          <p className="note">
            Aucune entité « todo. » trouvée. Créez une liste de courses dans Home Assistant
            (Paramètres → Appareils et services → Liste de tâches), puis retestez.
          </p>
        )}

        <div className="actions">
          <button type="button" className="bouton" onClick={enregistre} disabled={occupe}>
            Enregistrer
          </button>
        </div>
      </Section>

      {/* ── Agenda ─────────────────────────────────────────────────────── */}
      <Section
        titre="Agenda"
        description="Un lien à coller dans Google Agenda, Apple Calendrier ou Home Assistant."
      >
        <div className="bloc-url">
          <code>{ical.donnee!.url}</code>
          <button
            type="button"
            className="bouton icone"
            onClick={() => copieDansPressePapier(ical.donnee!.url, 'ical')}
            aria-label="Copier l’adresse de l’agenda"
          >
            {copie === 'ical' ? <Check size={18} strokeWidth={TRAIT} /> : <Copy size={18} strokeWidth={TRAIT} />}
          </button>
        </div>

        <p className="aide">
          <CalendarClock size={14} strokeWidth={TRAIT} /> Lecture seule. Les échéances y apparaissent
          en journée entière, avec leur récurrence. Les tâches sans date, elles, n’y figurent pas.
        </p>

        <details className="repli">
          <summary>Régénérer l’adresse</summary>
          <p className="alerte">
            <TriangleAlert size={14} strokeWidth={TRAIT} /> Tous les agendas déjà abonnés cesseront de
            se mettre à jour. Il faudra recoller la nouvelle adresse dans chacun.
          </p>
          <button
            type="button"
            className="bouton secondaire"
            disabled={occupe}
            onClick={async () => {
              setOccupe(true);
              try {
                await donnees.regenereIcal();
                ical.recharge();
              } finally {
                setOccupe(false);
              }
            }}
          >
            <RefreshCw size={18} strokeWidth={TRAIT} />
            Régénérer
          </button>
        </details>
      </Section>

      {/* ── Webhook ────────────────────────────────────────────────────── */}
      <Section
        titre="Webhook entrant"
        description="Pour qu’une automatisation crée une tâche : « la PAC a tourné 500 h, nettoyer les filtres »."
      >
        {webhook.donnee?.actif && webhook.donnee.url ? (
          <>
            <div className="bloc-url">
              <code>{webhook.donnee.url}</code>
              <button
                type="button"
                className="bouton icone"
                onClick={() => copieDansPressePapier(webhook.donnee!.url!, 'webhook')}
                aria-label="Copier l’adresse du webhook"
              >
                {copie === 'webhook' ? <Check size={18} strokeWidth={TRAIT} /> : <Copy size={18} strokeWidth={TRAIT} />}
              </button>
            </div>

            <p className="aide">
              <TriangleAlert size={14} strokeWidth={TRAIT} /> Cette adresse permet de <strong>créer</strong>
              {' '}des tâches. Contrairement à celle de l’agenda, elle ne se partage pas : gardez-la dans
              Home Assistant.
            </p>

            <details className="repli">
              <summary>Comment l’appeler</summary>
              <pre className="bloc-code">{`POST ${webhook.donnee.url}
Content-Type: application/json

{
  "titre": "Nettoyer les filtres de la PAC",
  "categorie": "chauffage",
  "cle": "pac-filtres"
}`}</pre>
              <p className="aide">
                Réutilisez la même <code>cle</code> à chaque déclenchement : la tâche ne sera créée
                qu’une fois, même si l’automatisation part vingt fois. Et si vous supprimez la tâche,
                elle ne reviendra pas.
              </p>
            </details>

            <div className="actions">
              <button
                type="button"
                className="bouton secondaire"
                disabled={occupe}
                onClick={async () => {
                  setOccupe(true);
                  try {
                    await donnees.activeWebhook();
                    webhook.recharge();
                  } finally {
                    setOccupe(false);
                  }
                }}
              >
                <RefreshCw size={18} strokeWidth={TRAIT} />
                Changer l’adresse
              </button>
              <button
                type="button"
                className="bouton secondaire"
                disabled={occupe}
                onClick={async () => {
                  setOccupe(true);
                  try {
                    await donnees.desactiveWebhook();
                    webhook.recharge();
                  } finally {
                    setOccupe(false);
                  }
                }}
              >
                Désactiver
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="aide">
              Désactivé. Rien n’écoute tant que vous ne l’avez pas activé — une fonctionnalité qui ne
              sert pas n’a pas à ouvrir de porte.
            </p>
            <button
              type="button"
              className="bouton"
              disabled={occupe}
              onClick={async () => {
                setOccupe(true);
                try {
                  await donnees.activeWebhook();
                  webhook.recharge();
                } finally {
                  setOccupe(false);
                }
              }}
            >
              <Webhook size={18} strokeWidth={TRAIT} />
              Activer
            </button>
          </>
        )}
      </Section>
    </>
  );
}
